import React, { type JSX } from 'react'
import { useMutation } from '@tanstack/react-query'
import dayjs from 'dayjs'
import {
  trackBlockDebitCard,
  trackCancelUnblockDebitCard,
  trackUnblockDebitCard,
} from '@mobi/account/Common/Analytics'
import { precommitmentUrl } from '@mobi/account/Utils'
import { post } from '@mobi/account/Utils'
import { Button } from '@mobi/component-library/Base/Buttons'
import { InfoBox } from '@mobi/component-library/Feedback/InfoBox'
import { Spinner } from '@mobi/component-library/Legacy/Spinner'
import { Modal } from '@mobi/component-library/Overlays/Modal'
import { type PrecommitmentResponse } from '../BetLimit/BetLimitPage'
import {
  BlockDebitCardFormStyled,
  ModalContentStyled,
  ParagraphStyled,
} from './BlockDebitCard.styles'
import { DEFAULT_COOLING_OFF_PERIOD_DAYS } from './constants'

export const BankCardBlock = ({
  data,
  hasDataFetchFailed,
}: {
  data: PrecommitmentResponse | undefined
  hasDataFetchFailed: boolean
}): JSX.Element => {
  const [isPopupShowing, setIsPopupShowing] = React.useState(false)
  const [precommitmentDetails, setPrecommitmentDetails] = React.useState(data)

  React.useEffect(() => {
    setPrecommitmentDetails(data)
  }, [data])

  const isDebitCardDepositsBlocked = precommitmentDetails?.BlockCreditCardDeposits
  const isDebitCardUnblockPendingCoolingOff = !!precommitmentDetails?.ProposedCreditCardReleaseDate

  return (
    <BlockDebitCardFormStyled>
      <ParagraphStyled>
        You can choose to block the use of debit cards on your account.
      </ParagraphStyled>
      {isPopupShowing && (
        <CardBlockPopup
          setIsPopupShowing={setIsPopupShowing}
          setPrecommitmentDetails={setPrecommitmentDetails}
          precommitmentDetails={precommitmentDetails}
        />
      )}
      {hasDataFetchFailed && (
        <InfoBox color='orange' size='sm'>
          Unable to display your debit card status. Please try again later.
        </InfoBox>
      )}
      {!hasDataFetchFailed && (
        <>
          <InfoBox
            color={precommitmentDetails?.BlockCreditCardDeposits ? 'orange' : 'blue'}
            size='sm'
          >
            {isDebitCardDepositsBlocked
              ? isDebitCardUnblockPendingCoolingOff
                ? `Your debit card block will be removed on ${precommitmentDetails?.ProposedCreditCardReleaseDate}`
                : 'Debit card deposits are currently blocked'
              : 'Debit card deposits are not currently blocked'}
          </InfoBox>
          <Button
            id='credit-card-block-button'
            color='primary'
            onClick={() => setIsPopupShowing(true)}
            isFullWidth
          >
            {isDebitCardDepositsBlocked
              ? isDebitCardUnblockPendingCoolingOff
                ? 'Cancel debit card unblock'
                : 'Remove debit card block'
              : 'Block debit card deposits'}
          </Button>
        </>
      )}
    </BlockDebitCardFormStyled>
  )
}

type CardBlockPopupProps = {
  setIsPopupShowing: (x: boolean) => void
  setPrecommitmentDetails: (x: PrecommitmentResponse) => void
  precommitmentDetails?: PrecommitmentResponse
}

const CardBlockPopup: React.FC<CardBlockPopupProps> = ({
  setIsPopupShowing,
  setPrecommitmentDetails,
  precommitmentDetails,
}) => {
  const isCancellingUnblock =
    precommitmentDetails?.BlockCreditCardDeposits &&
    !!precommitmentDetails?.ProposedCreditCardReleaseDate
  const isRemovingBlock =
    precommitmentDetails?.BlockCreditCardDeposits &&
    !precommitmentDetails.ProposedCreditCardReleaseDate

  const mutation = useMutation({
    mutationFn: () =>
      post<boolean, PrecommitmentResponse>(
        precommitmentUrl.updateCreditCardDeposits,
        !isRemovingBlock
      ),
    onSuccess: data => {
      if (isCancellingUnblock) {
        trackCancelUnblockDebitCard()
      } else if (isRemovingBlock) {
        trackUnblockDebitCard()
      } else {
        trackBlockDebitCard()
      }
      setIsPopupShowing(false)
      setPrecommitmentDetails(data)
    },
  })

  const headerTitle = isRemovingBlock ? 'Remove Debit Card Block' : 'Block Debit Card Deposits'
  const headerSubtitle = isRemovingBlock ? (
    <>
      Your debit card block will be removed on{' '}
      <strong>
        {dayjs()
          .add(
            precommitmentDetails?.CoolingOffPeriodDays || DEFAULT_COOLING_OFF_PERIOD_DAYS,
            'days'
          )
          .format('DD/MM/YYYY')}
      </strong>
      , after the mandatory cooling off period.
    </>
  ) : (
    <>
      I understand that debit card deposits to my account will be blocked effective immediately. If
      I choose to remove this block in future, it will be subject to a{' '}
      {precommitmentDetails?.CoolingOffPeriodDays || DEFAULT_COOLING_OFF_PERIOD_DAYS} day cooling
      off period.
    </>
  )

  return (
    <Modal
      headerIcon='warning'
      headerIconAlignment='center'
      headerTitle={headerTitle}
      headerSubtitle={<ModalContentStyled>{headerSubtitle}</ModalContentStyled>}
      headerTextAlignment='center'
      dismissStatus='disallowed'
      footerComponent={
        <>
          <Button
            id='credit-card-block-cancel'
            isFullWidth
            color='secondary_grey'
            onClick={() => setIsPopupShowing(false)}
          >
            Cancel
          </Button>

          <Button
            id='credit-card-block-save'
            isFullWidth
            color='primary'
            disabled={mutation.isPending}
            onClick={() => mutation.mutate()}
          >
            Confirm
          </Button>
        </>
      }
      isOpen
    >
      {mutation.isPending && <Spinner />}
    </Modal>
  )
}
